import { Injectable, Injector, computed, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { User as FirebaseUser } from '@angular/fire/auth';
import { User } from '@shared/definitions/user';
import { AuthService } from '@core/services/auth/auth.service';

/**
 * Phase 4 — first signal-based domain store.
 *
 * AuthStore is the single source of truth for authentication-related
 * state. It exposes **readonly signals** for reads and explicit
 * mutation methods for writes; components and services should never
 * touch the writable signals directly.
 *
 * Migration strategy:
 *  - New code subscribes to the readonly signals here.
 *  - `AuthService` keeps its legacy `BehaviorSubject` fields in sync
 *    with the store during the transition (Phase 4 → Phase 2 signal
 *    migration finish). Once every caller is migrated, the
 *    BehaviorSubjects in AuthService can be removed.
 */
@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly _user = signal<User | null>(null);
  private readonly _isAuthenticated = signal(false);
  private readonly _isNewUser = signal(false);
  private readonly _emailVerified = signal(false);
  private readonly _claimsReady = signal(false);
  private readonly _userLoaded = signal(false);
  /**
   * True after the first Firebase `onAuthStateChanged` callback for this
   * page load (user or null). Distinct from `isAuthenticated`: at boot the
   * latter starts `false` for ~100–300 ms even when a session will restore,
   * which used to flash the loading curtain off then on. Never reset on
   * logout — a hard reload to `/auth/login` starts a fresh store instance.
   */
  private readonly _authResolved = signal(false);

  private readonly injector = inject(Injector);
  private _service: AuthService | null = null;
  private get service(): AuthService {
    return (this._service ??= this.injector.get(AuthService));
  }

  /** Readonly signal view of the current user (null when signed-out). */
  readonly user = this._user.asReadonly();
  readonly isAuthenticated = this._isAuthenticated.asReadonly();
  readonly isNewUser = this._isNewUser.asReadonly();
  readonly emailVerified = this._emailVerified.asReadonly();
  /**
   * True once the Firebase ID token contains workspace custom claims.
   * Data services must wait for this before subscribing to workspace-scoped
   * collections; otherwise Firestore rejects the listener with
   * "Missing or insufficient permissions" on fresh signup.
   */
  readonly claimsReady = this._claimsReady.asReadonly();
  /**
   * True once the `users/{uid}` document has been read from Firestore at
   * least once for the current session (the snapshot listener has fired).
   * Onboarding guards MUST wait for this before reading `user.profile.*`:
   * `AuthService` first emits a partial user (email + uid, no `profile`),
   * so a guard that decided on that stale value would re-trigger the
   * user-onboarding wizard for a returning user on a fresh browser/device
   * (no IndexedDB cache → the partial user wins the race). Reset to false
   * between identities; the next snapshot flips it back to true.
   */
  readonly userLoaded = this._userLoaded.asReadonly();
  readonly authResolved = this._authResolved.asReadonly();

  /** Derived: is there enough of a user to render authenticated UI? */
  readonly hasUser = computed(() => {
    const u = this._user();
    return !!u && !!u.userUID;
  });

  setUser(user: User | null): void {
    this._user.set(user);
  }

  setIsAuthenticated(value: boolean): void {
    this._isAuthenticated.set(value);
  }

  setIsNewUser(value: boolean): void {
    this._isNewUser.set(value);
  }

  setEmailVerified(value: boolean): void {
    this._emailVerified.set(value);
  }

  setClaimsReady(value: boolean): void {
    this._claimsReady.set(value);
  }

  setUserLoaded(value: boolean): void {
    this._userLoaded.set(value);
  }

  /**
   * Mark Firebase auth as resolved for this page load. Idempotent — once
   * true, stays true for the lifetime of the store instance.
   */
  setAuthResolved(): void {
    if (!this._authResolved()) {
      this._authResolved.set(true);
    }
  }

  /**
   * Reset on sign-out. Explicit rather than clever: anyone reading the
   * code should see exactly what's cleared.
   * `authResolved` is intentionally NOT cleared — see field docs.
   */
  reset(): void {
    this._user.set(null);
    this._isAuthenticated.set(false);
    this._isNewUser.set(false);
    this._emailVerified.set(false);
    this._claimsReady.set(false);
    this._userLoaded.set(false);
  }

  /**
   * Observable view of `isAuthenticated`. Use the signal `isAuthenticated()` for
   * synchronous reads; this method is provided for legacy RxJS pipe consumers.
   */
  isAuthenticated$(): Observable<boolean> {
    return this.service.isAuthenticated();
  }

  /** Raw Firebase Auth `User` for the current session, or `null` if signed-out. */
  getCurrentFirebaseUser(): FirebaseUser | null {
    return this.service.getCurrentFirebaseUser();
  }

  /** Re-fetches the Firebase user to pick up fresh `emailVerified` state. */
  refreshEmailVerificationStatus(): Promise<boolean> {
    return this.service.refreshEmailVerificationStatus();
  }

  /**
   * Trigger the backend to (re)send the custom verification email.
   * `language` is the UI language the mail should be rendered in — callers pass
   * `translate.currentLang`; omitting it lets the backend use its default.
   */
  resendCustomEmailVerification(firstName?: string, language?: string): Promise<void> {
    return this.service.resendCustomEmailVerification(firstName, language);
  }

  /**
   * Ask for a fresh verification link using an expired one as proof — the
   * signed-out path used by `/auth/verify-email` (see the service method).
   */
  resendEmailVerificationFromToken(
    token: string,
    language?: string
  ): Promise<{ alreadyVerified: boolean }> {
    return this.service.resendEmailVerificationFromToken(token, language);
  }
}
